import * as React from "react";
import { X, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { log } from "@/utils/debug";

export interface Option<T = unknown> {
  value: string;
  label: string;
  code?: T;
}

interface MultiSelectProps<T = unknown> {
  options: Option<T>[];
  value: Option<T>[];
  onChange: (value: Option<T>[]) => void;
  placeholder?: string;
  isLoading?: boolean;
  onSearch?: (term: string) => void;
  disabled?: boolean;
  maxVisibleChips?: number; // show up to N chips in trigger; rest summarized
  includeAllOption?: boolean; // render a virtual All option controlling all items
}

export function MultiSelect<T>({
  options,
  value,
  onChange,
  placeholder = "Select...",
  isLoading = false,
  onSearch,
  disabled = false,
  maxVisibleChips = 3,
  includeAllOption = false,
}: MultiSelectProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm || onSearch) return options;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(lowerSearchTerm) || option.value.toLowerCase().includes(lowerSearchTerm),
    );
  }, [options, searchTerm, onSearch]);

  const handleSelect = React.useCallback(
    (option: Option<T>, e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      log.process("MultiSelect - handleSelect:", { option });
      const isSelected = value.some((item) => item.value === option.value);

      const newValue = isSelected ? value.filter((item) => item.value !== option.value) : [...value, { ...option }];

      onChange(newValue);
      setOpen(true);
    },
    [value, onChange],
  );

  const handleRemove = React.useCallback(
    (optionValue: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const newValue = value.filter((item) => item.value !== optionValue);
      onChange(newValue);
    },
    [value, onChange],
  );

  const totalCount = options.length;
  const selectedCount = value.length;
  const allSelected = selectedCount > 0 && selectedCount === totalCount;

  const selectAll = React.useCallback(() => {
    if (disabled) return;
    onChange(options.slice());
  }, [disabled, onChange, options]);

  const clearAll = React.useCallback(() => {
    if (disabled) return;
    onChange([]);
  }, [disabled, onChange]);

  return (
    <Popover open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between h-auto min-h-[2.5rem]", disabled && "opacity-70 cursor-not-allowed")}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 w-full">
            <div className="flex gap-1 max-w-[90%] overflow-x-auto overflow-y-hidden">
              {value.length === 0 && <span className="text-muted-foreground flex-shrink-0">{placeholder}</span>}
              {value.slice(0, Math.max(0, maxVisibleChips)).map((item) => (
                <Badge key={item.value} variant="secondary" className="mr-1 flex-shrink-0">
                  {item.label}
                  {!disabled && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="ml-1 rounded-full outline-none hover:bg-accent/50 p-0.5"
                      onClick={(e) => handleRemove(item.value, e)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleRemove(item.value, e as unknown as React.MouseEvent);
                        }
                      }}
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove {item.label}</span>
                    </span>
                  )}
                </Badge>
              ))}
              {value.length > maxVisibleChips && (
                <Badge variant="secondary" className="mr-1 flex-shrink-0">
                  +{value.length - maxVisibleChips} more
                </Badge>
              )}
            </div>
            <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 pointer-events-auto z-50"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      >
        <div className="sticky top-0 z-10 bg-background border-b p-2 flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={selectAll} disabled={disabled || isLoading || allSelected}>
            Select all
          </Button>
          <Button size="sm" variant="ghost" onClick={clearAll} disabled={disabled || selectedCount === 0}>
            Clear
          </Button>
          <span className="ml-auto text-xs text-muted-foreground">
            Selected {selectedCount} of {totalCount}
          </span>
        </div>
        <div className="p-2">
          <input
            ref={searchInputRef}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (onSearch) {
                onSearch(e.target.value);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
          />
        </div>
        <ScrollArea className="h-60" role="listbox" aria-multiselectable>
          <div className="p-1">
            {isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">No results found.</div>
            ) : (
              <>
                {includeAllOption && (
                  <div
                    key="__all__"
                    className={cn(
                      "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground",
                      allSelected && "bg-accent/50",
                      disabled && "pointer-events-none opacity-70",
                    )}
                    onClick={(e) => {
                      if (disabled) return;
                      e.preventDefault();
                      e.stopPropagation();
                      if (allSelected) {
                        clearAll();
                      } else {
                        selectAll();
                      }
                    }}
                    tabIndex={disabled ? -1 : 0}
                    role="option"
                    aria-selected={allSelected}
                  >
                    <Checkbox checked={allSelected} className="mr-2 pointer-events-none" disabled={disabled} />
                    <span>All</span>
                  </div>
                )}
                {filteredOptions.map((option) => {
                  const isSelected = value.some((item) => item.value === option.value);
                  return (
                    <div
                      key={option.value}
                      className={cn(
                        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground",
                        isSelected && "bg-accent/50",
                        disabled && "pointer-events-none opacity-70",
                      )}
                      onClick={(e) => !disabled && handleSelect(option, e)}
                      onKeyDown={(e) => {
                        if (!disabled && (e.key === "Enter" || e.key === " ")) {
                          handleSelect(option, e);
                        }
                      }}
                      tabIndex={disabled ? -1 : 0}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <Checkbox checked={isSelected} className="mr-2 pointer-events-none" disabled={disabled} />
                      <span>{option.label}</span>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
